import type { ApplicationContext } from '@hc-frontend/core-context';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import { CreateProspectParameters, LeadId } from '../../entities';
import type {
  CreateProspectRepository,
  CreateProspectResult,
} from '../../repositories';

export type CreateProspectUseCase = (
  parameters: CreateProspectParameters,
) => Promise<CreateProspectResult>;

type CreateProspectFactoryParams = Pick<ApplicationContext, 'log'> & {
  createProspect: CreateProspectRepository;
};

export function createProspectFactory({
  createProspect,
  log,
}: CreateProspectFactoryParams): CreateProspectUseCase {
  return async (parameters) => {
    const parsedParams = CreateProspectParameters.safeParse(parameters);

    if (!parsedParams.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToCreateProspect',
        data: {
          rootCause: parsedParams.error,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToCreateProspect',
            rootCause: parsedParams.error,
          },
        ],
      });
    }

    // Default source will be ORGANIC
    const source = parameters.source ?? 'ORGANIC';

    const result = await createProspect({
      ...parsedParams.data,
      source,
    });

    if (!result.success) {
      log({
        type: 'error',
        context: 'server',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
      return result;
    }

    const parsedResponse = LeadId.safeParse(result.data);
    if (!parsedResponse.success) {
      return createErrorOperationResult({
        errors: [
          { code: 'failedToCreateProspect', rootCause: parsedResponse.error },
        ],
      });
    }

    return createSuccessOperationResult(parsedResponse.data);
  };
}
